/**
 * Robust Error Boundary — fängt React-Render-Crashes ab und zeigt eine
 * lesbare Fehlermeldung anstelle eines weißen Bildschirms.
 *
 * Verwendung:
 *   <CaseEditorErrorBoundary>
 *     <CaseEditor />
 *   </CaseEditorErrorBoundary>
 *
 * Die Komponente loggt den vollen Stacktrace in die Browser-Console
 * (für Debugging) und zeigt dem Nutzer einen klaren Hinweis mit
 * konkreten Handlungsempfehlungen.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";

export default class CaseEditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Vollständiger Stack-Trace landet in der Browser-Console.
    // Wichtig für Production-Debugging — User sendet uns einen Screenshot.
    // eslint-disable-next-line no-console
    console.error("[CaseEditor] Render error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Hard reload — umgeht Browser-Cache.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  handleBack = () => {
    if (typeof window !== "undefined") {
      window.location.href = "/dashboard";
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errorMsg = this.state.error?.message || String(this.state.error || "Unbekannter Fehler");
    const stack = this.state.error?.stack || this.state.errorInfo?.componentStack || "";

    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="case-error-boundary">
        <Card className="max-w-2xl w-full p-8 border-rose-200">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-6 w-6 text-rose-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold text-zinc-900">
                Die Akte konnte nicht geladen werden
              </h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Beim Anzeigen dieser Akte ist ein technischer Fehler aufgetreten.
                Bitte probiere folgendes — meistens hilft eines davon:
              </p>
              <ol className="text-sm text-zinc-700 mt-3 space-y-1.5 list-decimal list-inside">
                <li>
                  <strong>Hartes Neuladen</strong>{" "}
                  <span className="text-zinc-500">
                    (Strg+Shift+R unter Windows / Cmd+Shift+R unter Mac)
                  </span>
                </li>
                <li>
                  <strong>Abmelden und neu anmelden</strong>
                </li>
                <li>
                  Falls das nicht hilft: Screenshot vom Browser-Console-Tab
                  (F12 → „Console") an den Support senden.
                </li>
              </ol>

              <div className="mt-5 rounded-lg bg-zinc-100 px-3 py-2 text-[11px] font-mono text-zinc-700 overflow-x-auto">
                {errorMsg}
              </div>

              {stack && (
                <details className="mt-2">
                  <summary className="text-[11px] text-zinc-400 cursor-pointer hover:text-zinc-600">
                    Technische Details
                  </summary>
                  <pre className="mt-2 rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-[10px] font-mono text-zinc-600 overflow-auto max-h-48 whitespace-pre-wrap">
                    {stack}
                  </pre>
                </details>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                <Button onClick={this.handleReload} className="brand-gradient-bg text-white" data-testid="case-error-reload">
                  <RefreshCw className="h-4 w-4 mr-2" /> Seite neu laden
                </Button>
                <Button onClick={this.handleBack} variant="outline" data-testid="case-error-back">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Zurück zum Dashboard
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}
